import React, { useState, useEffect, useContext } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { PurchaseMethod, PurchaseMethodFromEis } from './types'
import './style.css'
import { ClassNames } from '@emotion/react'

import { ThemeContext } from '../../../../components/ThemeContext'

import HoverTooltip from '../../../../components/HoverTooltip'

const formatDate = (isoString: string): string => {
    const date = new Date(isoString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
}

const TYPAL_KIND = new Map<string, string>([
    ['TSK', 'Конкурс'],
    ['TSA', 'Аукцион'],
    ['TSZK', 'Запрос котировок'],
    ['TSZP', 'Запрос предложений'],
    ['TSEP', 'Закупка у единственного поставщика (подрядчика, исполнителя)'],
])

export const loadPurchaseMethods = async (): Promise<PurchaseMethod> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/purchase_method',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Failed to fetch organization')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const OrgPurchaseMethodsPage: React.FC = () => {
    const navigate = useNavigate()
    const purchaseMethodsLoad = useLoaderData() as PurchaseMethod[]
    const [purchaseMethodsData, setPurchaseMethodsData] =
        useState<PurchaseMethod[]>(purchaseMethodsLoad)
    const [popupDeletePurchaseMethod, setPopupDeletePurchaseMethod] =
        useState(false)
    const [popupPurchaseMethodFromEis, setPopupPurchaseMethodFromEis] =
        useState(false)
    const [isDeleting, setIsDeleting] = useState(false) // Состояние для анимации загрузки

    const [selectedPurchaseMethodGuid, setSelectedPurchaseMethodGuid] =
        useState('')

    const [loadingGuid, setLoadingGuid] = useState<string | null>(null)

    const { theme } = useContext(ThemeContext)

    const [isLoading, setLoading] = useState(false) // Состояние для анимации загрузки
    const [isAdding, setIsAdding] = useState(false) // Состояние для анимации загрузки
    const [purchaseMethodFromEis, setPurchaseMethodFromEis] = useState<
        PurchaseMethodFromEis[]
    >([])

    const getNameTypalKind = (typal_kind: string): string => {
        return TYPAL_KIND.get(typal_kind) || ''
    }

    const getNamePurchaseMethod = (method_guid: string) => {
        const nameMethod = purchaseMethodsData.find(
            (s) => s.guid === method_guid
        )
        return nameMethod?.purchase_method_name || '-'
    }

    const handlePopupDeletePurchaseMethod = (method_guid: string) => {
        setPopupDeletePurchaseMethod(!popupDeletePurchaseMethod)
        // setErrorsInput({})
        setSelectedPurchaseMethodGuid(method_guid)
    }

    const handleDeletePurchaseMethod = async () => {
        setIsDeleting(true) // Включаем анимацию загрузки

        if (!selectedPurchaseMethodGuid) return

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/purchase_method/${selectedPurchaseMethodGuid}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при удалении')
            }
            setPopupDeletePurchaseMethod(false)
            setPurchaseMethodsData((prevStruct) =>
                prevStruct.filter(
                    (struct) => struct.guid !== selectedPurchaseMethodGuid
                )
            )
            setSelectedPurchaseMethodGuid('')
            console.log('Способ успешно удален')
        } catch (error) {
            console.error('Ошибка удаления способа:', error)
        } finally {
            setIsDeleting(false) // Отключаем анимацию загрузки
        }
    }

    const getPurchaseMethodFromEis = async () => {
        setLoading(true)

        const urlEis =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchase_method_from_eis`
        const urlEisMSP =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchase_method_from_eis_msp`

        try {
            // Выполняем оба запроса параллельно
            const [responseEis, responseEisMSP] = await Promise.all([
                fetch(urlEis, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
                fetch(urlEisMSP, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }),
            ])

            // Проверяем ответы
            if (!responseEis.ok || !responseEisMSP.ok) {
                throw new Error(
                    `Failed to get purchase methods. Status: ${responseEis.status}, ${responseEisMSP.status}`
                )
            }

            // Получаем данные из обоих запросов
            const dataEis = await responseEis.json()
            const dataEisMSP = await responseEisMSP.json()

            // Объединяем результаты
            const combinedData = [...dataEis, ...dataEisMSP]

            // Обновляем состояние
            setPurchaseMethodFromEis(combinedData)
        } catch (error) {
            console.error('Error while getting purchase methods:', error)
        } finally {
            setLoading(false) // Сбрасываем состояние загрузки
        }
    }

    const addPurchaseMethodFromEis = async (
        purchaseItem: PurchaseMethod
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL + `/api/backend/v1/purchase_method`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(purchaseItem), // Добавлено тело запроса
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }
            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setIsAdding(false)
        } finally {
        }
    }

    const updatePurchaseMethodFromEis = async (
        purchaseItem: PurchaseMethod
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchase_method/${purchaseItem.guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(purchaseItem), // Добавлено тело запроса
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to create new position from. Status: ${response.status}`
                )
            }
            console.log('Position created successfully')
        } catch (error) {
            console.error('Error while creating position:', error)
            setIsAdding(false)
        } finally {
        }
    }

    const mapPurchaseMethodFromEisToPurchaseMethod = (
        item: PurchaseMethodFromEis
    ): PurchaseMethod => {
        return {
            guid: item.guid,
            purchase_method_code: item.code,
            purchase_method_name: item.name,
            purchase_method_short_name: '',
            typal: item.typal,
            typal_kind: item.typal_kind,
            is_electronic: item.is_electronic,
            closed_purchase: item.closed_purchase,
            competitive: item.competitive,
            has_phases: item.has_phases,
            purchase_smb_type: item.name.includes(
                'малого и среднего предпринимательства'
            ),
            minimum_deadline_days: 0, // Предположительно значение отсутствует в исходных данных
            days_type: !item.name.includes(
                'малого и среднего предпринимательства'
            ), // Пример значения по умолчанию
            maximum_contract_price: 0, // Пример значения по умолчанию
            maximum_amount_request_security: 0, // Пример значения по умолчанию
            maximum_amount_contract_security: 0, // Пример значения по умолчанию
            required_document_tz: false, // Пример значения по умолчанию
            required_document_nmcd: false, // Пример значения по умолчанию
            required_document_en: false, // Пример значения по умолчанию
            required_document_contract_project: false, // Пример значения по умолчанию
            modification_description: item.user_modification_reason || '',
            modification_date: item.change_date_time || '',
            is_active: true,
        }
    }

    // Функция для загрузки данных и обновления таблицы
    const reloadPurchaseMethods = async () => {
        setLoading(true) // Включаем индикатор загрузки
        try {
            const data =
                (await loadPurchaseMethods()) as unknown as PurchaseMethod[] // Загружаем данные через API
            setPurchaseMethodsData(data) // Обновляем состояние таблицы
        } catch (error) {
            console.error('Error while reloading purchase methods:', error)
        } finally {
            setLoading(false) // Выключаем индикатор загрузки
        }
    }

    const handleAddClick = async () => {
        setIsAdding(true)
        try {
            const mappedItems = purchaseMethodFromEis.map(
                mapPurchaseMethodFromEisToPurchaseMethod
            )

            const tasks = mappedItems.map(async (item) => {
                const existingItem = purchaseMethodsData.find(
                    (existing) =>
                        existing.purchase_method_code ===
                        item.purchase_method_code
                )

                if (existingItem) {
                    console.log(
                        `Updating existing item with code: ${item.purchase_method_code}`
                    )
                    await updatePurchaseMethodFromEis({
                        ...item,
                        guid: existingItem.guid,
                    })
                } else {
                    console.log(
                        `Adding new item with code: ${item.purchase_method_code}`
                    )
                    await addPurchaseMethodFromEis(item)
                }
            })

            await Promise.all(tasks)

            console.log('All items processed successfully')

            // После добавления/обновления заново загружаем данные
            await reloadPurchaseMethods()

            setPurchaseMethodFromEis([])
        } catch (error) {
            console.error('Error while processing purchase methods:', error)
        } finally {
            setIsAdding(false)
            setPopupPurchaseMethodFromEis(false)
        }
    }

    const handlePopupPurchaseMethodFromEis = () => {
        getPurchaseMethodFromEis()
        setPopupPurchaseMethodFromEis(!popupPurchaseMethodFromEis)
    }

    const handleRemoveFromList = (guid: string) => {
        setPurchaseMethodFromEis((prev) =>
            prev.filter((item) => item.guid !== guid)
        )
    }

    const handleChangeStatusPurchaseMethod = async (
        guid: string,
        status: boolean
    ) => {
        setLoading(true) // Включаем индикатор загрузки
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/purchase_method/${guid}`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        is_active: status,
                    }), // Добавлено тело запроса
                }
            )
            if (!response.ok) {
                throw new Error('Ошибка при изменении способа закупки')
            }

            console.log('Способ закупки успешно изменен')
        } catch (error) {
            console.error('Ошибка при изменении способа закупки:', error)
        } finally {
            setLoading(false) // Включаем индикатор загрузки
            reloadPurchaseMethods()
        }
    }

    useEffect(() => {
        setLoadingGuid(null) // Сбросить loadingGuid
    }, [purchaseMethodsData])

    return (
        <div className="flex flex-col text-[#181818] dark:text-[#FFFFFF] h-full">
            <div className="bg-[#e5e5e5] dark:bg-[#111111]">
                <div className="w-auto h-20 mx-6 flex flex-row gap-x-5 ">
                    <div className="w-60 h-20 my-auto content-center ">
                        <p className="text-sm text-[#181818] dark:text-[#FFFFFF]">
                            Реестр способов закупки
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex flex-1 justify-center max-h-[calc(100vh_-_80px)] p-5 bg-[#f0f3f5] dark:bg-[#181818]">
                <div className="flex flex-col h-full w-full space-y-10 ">
                    {/* // body */}
                    <div className="flex h-[calc(100vh_-_120px)] bg-[#f0f3f5] dark:bg-[#181818]">
                        {/* table */}
                        <div className="flex flex-col w-full min-w-fit">
                            {/* toolbar */}
                            <div className="flex flex-row gap-1 p-2 h-10 rounded-t-lg text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                                {/* Add */}
                                <HoverTooltip tooltipText="Добавить способ">
                                    <button
                                        className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={() =>
                                            navigate(
                                                '/hub/org/methods/new/edit'
                                            )
                                        }
                                    >
                                        <svg
                                            className="w-3 h-3"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            id="plus"
                                        >
                                            <path
                                                fill={
                                                    theme == 'dark'
                                                        ? '#FFFFFF'
                                                        : '#181818'
                                                }
                                                d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                            ></path>
                                        </svg>
                                    </button>
                                </HoverTooltip>

                                {/* Add */}
                                <HoverTooltip tooltipText="Добавить/Обновить из ЕИС">
                                    <button
                                        className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={
                                            handlePopupPurchaseMethodFromEis
                                        }
                                    >
                                        {isAdding ? (
                                            <svg
                                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        ) : (
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 22 22"
                                                id="update"
                                            >
                                                <path
                                                    fill={
                                                        theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z"
                                                ></path>
                                            </svg>
                                        )}
                                    </button>
                                </HoverTooltip>
                            </div>

                            {/* name */}
                            <div className="flex flex-row gap-1 p-2 h-10 text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#e4e7ea] dark:bg-[#262427]">
                                <div className="grid w-10 place-items-center text-center">
                                    №
                                </div>
                                <div className="grid w-12 place-items-center text-center">
                                    Id способа
                                </div>
                                <div className="grid flex-1 min-w-80 place-items-center text-center">
                                    Наименование способа
                                </div>
                                <div className="grid flex-1 min-w-40 place-items-center text-center">
                                    Сокращенное наименование способа
                                </div>
                                <div className="grid w-32 place-items-center text-center">
                                    Статус
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Тип способа закупки
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Вид способа закупки
                                </div>
                                <div className="grid w-52 place-items-center text-center">
                                    Вид типового способа
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Тип конкурентности
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Форма проведения
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Ограничение по МСП
                                </div>
                                <div className="grid w-28 place-items-center text-center">
                                    Дата изменения
                                </div>
                                <div className="grid w-12 place-items-center text-center"></div>
                            </div>

                            {/* data */}
                            <div className="flex flex-col gap-1 py-1 h-dvh overflow-y-auto bg-[#e4e6e8] dark:bg-[#1D1C1E] custom-scrollbar">
                                {/* Вывод пользователей */}
                                {purchaseMethodsData.map((method, index) => (
                                    <div
                                        key={method.guid}
                                        className="flex flex-row cursor-pointer gap-1 mx-1 px-1 rounded text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                    >
                                        <div
                                            className="flex flex-row flex-1 gap-1 py-1"
                                            onClick={() => {
                                                navigate(
                                                    `/hub/org/methods/${method.guid}/edit`
                                                )
                                            }}
                                        >
                                            <div className="grid w-10 place-items-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                {index + 1}
                                            </div>
                                            <div className="grid w-12 place-items-center text-center">
                                                {method.purchase_method_code}
                                            </div>
                                            <div className="grid flex-1 place-items-center justify-items-start text-left px-2 py-2">
                                                {method.purchase_method_name}
                                            </div>
                                            <div className="grid flex-1 place-items-center justify-items-start text-left px-2 py-2">
                                                {
                                                    method.purchase_method_short_name
                                                }
                                            </div>
                                            <div className="flex w-32 items-center justify-center px-2 rounded gap-1">
                                                <button
                                                    className="group flex flex-row items-center justify-center gap-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        setLoadingGuid(
                                                            method.guid
                                                        ) // Установить loadingGuid
                                                        handleChangeStatusPurchaseMethod(
                                                            method.guid,
                                                            !method.is_active
                                                        )
                                                    }}
                                                >
                                                    {loadingGuid ===
                                                    method.guid ? (
                                                        <div className="flex w-8 h-8 rounded-full items-center justify-center">
                                                            <svg
                                                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                ></path>
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`flex w-8 h-8 rounded-full items-center justify-center ${
                                                                method.is_active
                                                                    ? 'group-hover:bg-[#83d2b766] dark:group-hover:bg-[#187A3366]'
                                                                    : 'group-hover:bg-[#ffc3a966] dark:group-hover:bg-[#dc742f66]'
                                                            }`}
                                                        >
                                                            <div
                                                                className={`flex w-5 h-5 rounded-full items-center justify-center text-[#181818] dark:text-[#FFFFFF] ${
                                                                    method.is_active
                                                                        ? 'bg-[#83d2b7] dark:bg-[#187A33]'
                                                                        : 'bg-[#ffc3a9] dark:bg-[#dc742f]'
                                                                }`}
                                                            >
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 24 24"
                                                                    className="h-4 w-4"
                                                                >
                                                                    <path
                                                                        fill={
                                                                            theme ==
                                                                            'dark'
                                                                                ? '#FFFFFF'
                                                                                : '#181818'
                                                                        }
                                                                        d={
                                                                            method.is_active
                                                                                ? 'M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z'
                                                                                : 'M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z'
                                                                        }
                                                                    ></path>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        {method.is_active
                                                            ? 'Активный'
                                                            : 'Не активный'}
                                                    </div>
                                                </button>
                                            </div>
                                            <div className="grid w-28 place-items-center text-center px-2 ">
                                                {method.typal
                                                    ? 'Типовой'
                                                    : 'Иной'}
                                            </div>
                                            <div className="grid w-28 place-items-center text-center px-2 ">
                                                {method.closed_purchase
                                                    ? 'Закрытый'
                                                    : 'Открытый'}
                                            </div>
                                            <div className="grid w-52 place-items-center text-center px-2">
                                                {getNameTypalKind(
                                                    method.typal_kind
                                                )}
                                            </div>
                                            <div className="grid w-28 place-items-center text-center px-2 ">
                                                {method.competitive
                                                    ? 'Конкурентный'
                                                    : 'Неконкурентный'}
                                            </div>
                                            <div className="grid w-28 place-items-center text-center px-2 ">
                                                {method.is_electronic
                                                    ? 'В электронной форме'
                                                    : 'В бумажной форме'}
                                            </div>
                                            <div className="grid w-28 place-items-center text-center ">
                                                {method.purchase_smb_type
                                                    ? 'Есть'
                                                    : 'Нет'}
                                            </div>
                                            <div className="grid w-28 place-items-center text-center text-[#6A6A6A] dark:text-[#8A8A8A] py-2">
                                                {method.modification_date
                                                    ?.slice(0, 10)
                                                    .split('-')
                                                    .reverse()
                                                    .join('-')
                                                    .replace(/[\-]/g, '.')}
                                                <br />
                                                {method.modification_date?.slice(
                                                    11,
                                                    16
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid w-10 place-items-center items-center">
                                            <HoverTooltip
                                                tooltipText="Удалить"
                                                align="left"
                                            >
                                                <button
                                                    className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                    onClick={() =>
                                                        handlePopupDeletePurchaseMethod(
                                                            method.guid
                                                        )
                                                    }
                                                >
                                                    <svg
                                                        className="w-3 h-3"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        id="trash-alt"
                                                    >
                                                        <path
                                                            fill={
                                                                theme == 'dark'
                                                                    ? '#FFFFFF'
                                                                    : '#181818'
                                                            }
                                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                        ></path>
                                                    </svg>
                                                </button>
                                            </HoverTooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* buttons */}
                            <div className="flex flex-row gap-1 p-2 rounded-b-lg h-10 space-x-2 text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                                <button
                                    className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() =>
                                        navigate('/hub/org/methods/new/edit')
                                    }
                                >
                                    <svg
                                        className="h-3 w-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="plus"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>{' '}
                                    Создать способ закупки
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {popupDeletePurchaseMethod && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#262427] min-w-[300px] max-w-[600px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="times-circle"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Удаление способа закупки
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="flex flex-col text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                Вы точно хотите удалить способ:
                                <span className="text-[#181818] dark:text-[#FFFFFF]">
                                    {`- ${getNamePurchaseMethod(selectedPurchaseMethodGuid)} ?`}
                                </span>
                            </label>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-3">
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] shadow-md text-xs text-[#181818] dark:text-[#FFFFFF] ml-2 items-center justify-center bg-[#701F1F] hover:bg-[#9E2C2C]`}
                                onClick={handleDeletePurchaseMethod} // Вызвать handleAdd
                            >
                                {isDeleting ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Удалить'
                                )}
                            </button>
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 text-[#181818] dark:text-[#FFFFFF] text-xs rounded-[10px] justify-center items-center gap-1 bg-[#494849] hover:bg-[#656365]"
                                onClick={() =>
                                    handlePopupDeletePurchaseMethod('')
                                }
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {popupPurchaseMethodFromEis && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={handlePopupPurchaseMethodFromEis}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[600px] max-h-[800px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Способы закупок в ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                Какие способы закупок добавить/обновить?
                            </label>
                            <div className="flex flex-row w-full space-x-5 pt-2">
                                <div className="flex flex-col w-full">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 bg-[#d2d5d8] dark:bg-[#333033] text-[#6A6A6A] dark:text-[#8A8A8A] text-[10px] px-1 py-2 rounded-t-lg">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-12 items-center justify-center text-center">
                                            Id способа
                                        </div>
                                        <div className="flex flex-1 items-center justify-start text-center">
                                            Название способо
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Дата создания
                                        </div>
                                        <div className="flex w-10 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[400px] gap-1 py-1 px-1 grow bg-[#e4e6e8] dark:bg-[#1D1C1E] rounded-b-lg overflow-y-auto custom-scrollbar">
                                        <div className="flex flex-col w-full space-y-1">
                                            {purchaseMethodFromEis?.map(
                                                (item, index) => (
                                                    <div
                                                        key={item.guid}
                                                        className="flex flex-row w-full rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                    >
                                                        <div className="flex flex-row w-full">
                                                            <div className="flex flex-row flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px] py-2 pr-1">
                                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="flex w-12 items-center justify-center">
                                                                    {item.code
                                                                        ? item.code
                                                                        : '-'}
                                                                </div>
                                                                <div className="flex  flex-1 items-center justify-left p-1">
                                                                    {item.name
                                                                        ? item.name
                                                                        : '-'}
                                                                </div>
                                                                <div className="flex w-24 items-center justify-end">
                                                                    {item.create_date_time
                                                                        ? formatDate(
                                                                              item.create_date_time
                                                                          )
                                                                        : '-'}
                                                                </div>
                                                                <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                    <HoverTooltip
                                                                        tooltipText="Удалить"
                                                                        align="left"
                                                                    >
                                                                        <button
                                                                            className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                                            onClick={() =>
                                                                                handleRemoveFromList(
                                                                                    item.guid
                                                                                )
                                                                            }
                                                                        >
                                                                            <svg
                                                                                className="w-3 h-3"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                viewBox="0 0 24 24"
                                                                                id="trash-alt"
                                                                            >
                                                                                <path
                                                                                    fill={
                                                                                        theme ==
                                                                                        'dark'
                                                                                            ? '#FFFFFF'
                                                                                            : '#181818'
                                                                                    }
                                                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                                ></path>
                                                                            </svg>
                                                                        </button>
                                                                    </HoverTooltip>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row w-full mt-5 text-right items-center justify-end gap-3">
                            {isAdding ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handlePopupPurchaseMethodFromEis}
                                >
                                    Отмена
                                </button>
                            )}
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 ml-2 justify-center items-center rounded-[10px] shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]`}
                                onClick={handleAddClick} // Вызвать handleCopy
                                disabled={isAdding ? true : false}
                            >
                                {isAdding ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Добавить/Обновить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default OrgPurchaseMethodsPage
